/*
 * IndexedDB-backed write queue for the Edotask offline mode (Phase 4b).
 *
 * Why IndexedDB and not localStorage?
 *   - Survives Safari's aggressive eviction better than localStorage for PWAs.
 *   - Allows storing structured objects (request body, headers, optimistic payload)
 *     without manual JSON.stringify gymnastics.
 *   - Has space limits in the hundreds of MB, more than enough for a task queue.
 *
 * Queue shape: array of operation objects:
 *   {
 *     op_id:           string  (client-generated, unique)
 *     method:          'POST' | 'PUT' | 'DELETE'
 *     url:             string  (absolute URL the request will fire at)
 *     body:            any     (JSON-serialisable request body, or null)
 *     optimistic_id:   string? (the client-side UUID issued for POSTed entities,
 *                              so that a follow-up PUT/DELETE on that ID can be
 *                              mapped to the real server ID after drain)
 *     entity_kind:     'task' | 'contact'
 *     snapshot_updated_at: string? (server updated_at observed at queue time,
 *                              used for optimistic concurrency / conflict detect)
 *     queued_at:       number  (Date.now())
 *     attempts:        number  (incremented on each drain attempt)
 *     last_error:      string?
 *   }
 */

const DB_NAME = 'edotask_offline';
const DB_VERSION = 2;
const QUEUE_STORE = 'pending_ops';
const MAPPING_STORE = 'id_mappings'; // optimistic_id -> server_id
const READ_CACHE_STORE = 'read_cache'; // cached GET responses keyed by URL

let dbPromise = null;

const openDb = () => {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      // Some embedded WebViews disable IndexedDB. Reject loudly so the caller
      // can fall back to a no-op queue.
      reject(new Error('IndexedDB is unavailable'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(QUEUE_STORE)) {
        const store = db.createObjectStore(QUEUE_STORE, { keyPath: 'op_id' });
        store.createIndex('queued_at', 'queued_at');
      }
      if (!db.objectStoreNames.contains(MAPPING_STORE)) {
        db.createObjectStore(MAPPING_STORE, { keyPath: 'optimistic_id' });
      }
      if (!db.objectStoreNames.contains(READ_CACHE_STORE)) {
        db.createObjectStore(READ_CACHE_STORE, { keyPath: 'cache_key' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
};

const tx = async (storeName, mode, fn) => {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const t = db.transaction(storeName, mode);
    const store = t.objectStore(storeName);
    let result;
    Promise.resolve(fn(store, t))
      .then((r) => { result = r; })
      .catch(reject);
    t.oncomplete = () => resolve(result);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error || new Error('transaction aborted'));
  });
};

export const enqueueOp = (op) => tx(QUEUE_STORE, 'readwrite', (store) => {
  store.put({ ...op, attempts: op.attempts || 0 });
});

export const removeOp = (op_id) => tx(QUEUE_STORE, 'readwrite', (store) => store.delete(op_id));

export const updateOp = (op) => tx(QUEUE_STORE, 'readwrite', (store) => store.put(op));

export const listOps = () => tx(QUEUE_STORE, 'readonly', (store) => new Promise((res) => {
  const all = [];
  const cursorReq = store.index('queued_at').openCursor();
  cursorReq.onsuccess = (ev) => {
    const cursor = ev.target.result;
    if (cursor) {
      all.push(cursor.value);
      cursor.continue();
    } else {
      res(all);
    }
  };
}));

export const queueSize = async () => {
  try {
    const ops = await listOps();
    return ops.length;
  } catch {
    return 0;
  }
};

export const recordMapping = (optimistic_id, server_id) =>
  tx(MAPPING_STORE, 'readwrite', (store) => store.put({ optimistic_id, server_id }));

export const resolveMapping = (optimistic_id) =>
  tx(MAPPING_STORE, 'readonly', (store) => new Promise((res) => {
    const req = store.get(optimistic_id);
    req.onsuccess = () => res(req.result ? req.result.server_id : null);
  }));

export const clearAll = async () => {
  try {
    await tx(QUEUE_STORE, 'readwrite', (store) => store.clear());
    await tx(MAPPING_STORE, 'readwrite', (store) => store.clear());
    await tx(READ_CACHE_STORE, 'readwrite', (store) => store.clear());
  } catch {
    /* noop */
  }
};

// --- Read cache --------------------------------------------------------------
// Stores successful GET responses so the app can serve them when offline.
// `cache_key` is the request pathname + canonical search string, so we are
// origin-agnostic (works even when the API is on a different host than the
// frontend — which is the case in production for Edotask).

export const putReadCache = (cache_key, data) =>
  tx(READ_CACHE_STORE, 'readwrite', (store) => store.put({
    cache_key,
    data,
    cached_at: Date.now(),
  }));

export const getReadCache = (cache_key) =>
  tx(READ_CACHE_STORE, 'readonly', (store) => new Promise((res) => {
    const req = store.get(cache_key);
    req.onsuccess = () => res(req.result || null);
    req.onerror = () => res(null);
  }));

export const listReadCacheKeys = (prefix) =>
  tx(READ_CACHE_STORE, 'readonly', (store) => new Promise((res) => {
    const out = [];
    const cursorReq = store.openCursor();
    cursorReq.onsuccess = (ev) => {
      const cursor = ev.target.result;
      if (cursor) {
        if (!prefix || cursor.value.cache_key.startsWith(prefix)) {
          out.push(cursor.value);
        }
        cursor.continue();
      } else {
        res(out);
      }
    };
    cursorReq.onerror = () => res(out);
  }));
